import { useState, useEffect, useRef, useCallback } from 'react';
import { useDataCache } from '../../../../contexts/BreakdownDataCacheContext';
import { ElementWithUserNames } from '../../../../types/element.types';
import { getElementsFromCache } from '../../../../utils/getElementsFromCache';
import { sortElements } from '../../../../utils/elementSortUtils';

/**
 * 场次元素数据获取 Hook
 * 
 * 功能：
 * - 从缓存获取和订阅场次元素数据
 * - 订阅 SceneCache 获取 breakdownId（从 sceneInfo.sceneBelongBreakdownID）
 * - 订阅 ElementCache 和 ElementRelationCache
 * - 使用 getElementsFromCache 组装数据
 * - 应用排序逻辑
 * 
 * @param sceneId 场次ID
 * @returns elements 状态和 breakdownId
 */
export const useElementsData = (sceneId: string) => {
  const {
    getSceneCache,
    subscribeSceneCache,
    getElementCache,
    subscribeElementCache,
    getElementRelationCache,
    subscribeElementRelationCache,
  } = useDataCache();

  const [elements, setElements] = useState<ElementWithUserNames[]>([]);
  const [breakdownId, setBreakdownId] = useState<string | null>(null);

  // 使用 ref 存储防抖定时器
  const updateTimerRef = useRef<NodeJS.Timeout | null>(null);

  // 从 SceneCache 或 BreakdownCache 获取 breakdownId
  const getBreakdownIdFromCache = useCallback((sceneId: string): string | null => {
    // 优先从 SceneCache 获取
    const sceneCache = getSceneCache(sceneId);
    if (sceneCache?.sceneInfo?.sceneBelongBreakdownID) {
      return sceneCache.sceneInfo.sceneBelongBreakdownID;
    }

    // 如果 SceneCache 中没有，尝试从 BreakdownCache 中查找
    // 遍历所有可能的 breakdownId（通过检查所有 BreakdownCache）
    // 由于 BreakdownCache 是按 breakdownId 组织的，我们需要知道 breakdownId 才能查找
    // 但我们可以通过检查场景的 sceneBelongBreakdownID 来推断
    // 实际上，更好的方法是在 useBreakdownDataLoader 中设置 SceneCache
    // 这里我们暂时返回 null，等待 SceneCache 更新
    return null;
  }, [getSceneCache]);

  // 订阅 SceneCache 获取 breakdownId
  useEffect(() => {
    if (!sceneId) return;

    const handleSceneCacheUpdate = () => {
      const foundBreakdownId = getBreakdownIdFromCache(sceneId);
      setBreakdownId(foundBreakdownId);
    };

    // 立即执行一次
    handleSceneCacheUpdate();

    // 订阅 SceneCache 更新
    const unsubscribe = subscribeSceneCache(sceneId, handleSceneCacheUpdate);

    return () => {
      unsubscribe();
    };
  }, [sceneId, getBreakdownIdFromCache, subscribeSceneCache]);

  // 订阅 ElementCache 和 ElementRelationCache，组装数据
  useEffect(() => {
    if (!sceneId || !breakdownId) {
      setElements([]);
      return;
    }

    const updateElements = () => {
      // 清除之前的定时器
      if (updateTimerRef.current) {
        clearTimeout(updateTimerRef.current);
      }

      // 使用防抖，避免短时间内多次更新
      updateTimerRef.current = setTimeout(() => {
        const relationCache = getElementRelationCache(breakdownId);
        const itemCache = getElementCache(breakdownId);

        // 使用工具函数从缓存获取该场次的元素
        const items = getElementsFromCache(sceneId, breakdownId, relationCache, itemCache);

        // 应用排序：按类型顺序和名称排序
        const sortedElements = sortElements(items);

        setElements(sortedElements);
      }, 0); // 使用 0ms 延迟，确保在下一个事件循环中执行
    };

    // 立即执行一次
    updateElements();

    // 订阅两个缓存的更新
    const unsubscribeRelation = subscribeElementRelationCache(breakdownId, updateElements);
    const unsubscribeItem = subscribeElementCache(breakdownId, updateElements);

    return () => {
      unsubscribeRelation();
      unsubscribeItem();
      // 清理定时器
      if (updateTimerRef.current) {
        clearTimeout(updateTimerRef.current);
      }
    };
  }, [
    sceneId,
    breakdownId,
    getElementCache,
    getElementRelationCache,
    subscribeElementCache,
    subscribeElementRelationCache,
  ]);

  return {
    elements,
    breakdownId,
  };
};

